import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import classNames from "classnames";
import MaskedInput from "react-text-mask";

import { PaymentService, ClientService, InvoiceService, JobService } from "@services";
import utils from "@utils";
import { PAYMENTS_TAB_MENU, URLS } from "@constants";
import { Select } from "@components/common";
import OrganizationService from "@services/OrganizationService";
import {
	ApplyPaymentsModal,
	HeaderGrid,
	TabMenu,
	SearchField,
	ScrollDetect,
	ConfirmationModal,
} from "../../../components";
import { EmailInvoiceModal } from "../invoices/components/index";
import "./NonRKPayInvoiceList.scss";
import AddToInvoiceModal from "../AddToInvoicie/AddToInvoiceModal";
import { FEATURES, PAYMENTS_V4_TAB_MENU } from "../../../js/constants";

const displayCount = 25;

let manualInvoicesCancel;
let getClientsCancel;
let getAgenciesCancel;

const nonRKPayInvoicesTypesIndex = (invoicingVersion, countKey) => {
	if (invoicingVersion === 2) {
		return ["estimates", "awaiting-payment", "paid"].findIndex(key => key === countKey);
	}
	return [
		"estimates",
		"pending-finalization",
		"awaiting-payment",
		"paid",
		"pending-jobs",
	].findIndex(key => key === countKey);
};

const NON_RK_PAY_INVOICES_TYPES = {
	ESTIMATES: 0,
	PENDING_FINALIZATION: 1,
	AWAITING_PAYMENT: 2,
	PAID: 3,
	PENDING_JOBS: 4,
};

const nonRKPayInvoicesTypes = invoicingVersion => {
	if (invoicingVersion === 2) {
		return [
			{
				type: nonRKPayInvoicesTypesIndex(invoicingVersion, "estimates"),
				countKey: "estimates",
				filter: { paid: undefined, isEstimate: true, finalized: undefined },
				displayName: "Estimates",
				dataTestId: "payments_page__estimates_tab",
			},
			{
				type: nonRKPayInvoicesTypesIndex(invoicingVersion, "awaiting-payment"),
				countKey: "awaiting-payment",
				filter: {
					paid: false,
					isEstimate: undefined,
					finalized: undefined,
					voided: undefined,
					vehicleInvoicingRequired: undefined,
					archived: undefined,
				},
				displayName: "Awaiting Payment",
				dataTestId: "payment_page__awaiting_payment_tab",
			},
			{
				type: nonRKPayInvoicesTypesIndex(invoicingVersion, "paid"),
				countKey: "paid",
				filter: {
					paid: true,
					isEstimate: undefined,
					finalized: undefined,
					voided: undefined,
					vehicleInvoicingRequired: undefined,
				},
				displayName: "Paid",
				dataTestId: "payments_page__paid_tab",
			},
		];
	}
	return [
		{
			type: nonRKPayInvoicesTypesIndex(invoicingVersion, "estimates"),
			countKey: "estimates",
			filter: {
				paid: undefined,
				isEstimate: true,
				finalized: undefined,
				pendingJobs: undefined,
				pendingFinalization: undefined,
				payment: undefined,
				vehicleInvoicingRequired: undefined,
			},
			displayName: "Estimates",
		},
		{
			type: nonRKPayInvoicesTypesIndex(invoicingVersion, "pending-jobs"),
			countKey: "pending-jobs",
			filter: {
				paid: undefined,
				isEstimate: undefined,
				finalized: false,
				voided: undefined,
				pendingJobs: true,
				pendingFinalization: undefined,
				payment: undefined,
				vehicleInvoicingRequired: undefined,
			},
			displayName: "Jobs Pending Invoice",
		},
		{
			type: nonRKPayInvoicesTypesIndex(invoicingVersion, "pending-finalization"),
			countKey: "pending-finalization",
			filter: {
				paid: undefined,
				isEstimate: undefined,
				finalized: false,
				voided: undefined,
				pendingJobs: undefined,
				pendingFinalization: true,
				payment: undefined,
				vehicleInvoicingRequired: undefined,
			},
			displayName: "Pending Finalization",
		},
		{
			type: nonRKPayInvoicesTypesIndex(invoicingVersion, "awaiting-payment"),
			countKey: "awaiting-payment",
			filter: {
				paid: false,
				isEstimate: undefined,
				finalized: true,
				voided: undefined,
				pendingJobs: undefined,
				pendingFinalization: undefined,
				payment: true,
				vehicleInvoicingRequired: undefined,
			},
			displayName: "Awaiting Payment",
		},
		{
			type: nonRKPayInvoicesTypesIndex(invoicingVersion, "paid"),
			countKey: "paid",
			filter: {
				paid: true,
				isEstimate: undefined,
				finalized: true,
				voided: undefined,
				pendingJobs: undefined,
				pendingFinalization: undefined,
				payment: undefined,
				vehicleInvoicingRequired: undefined,
			},
			displayName: "Paid",
		},
	];
};

class NonRKPayInvoiceList extends Component {
	constructor(props) {
		super(props);
		const {
			user,
			location: { query },
		} = props;
		const isInvoicingV3 = user.Features.some(f => f.toLowerCase() === "invoicingv3");

		// PBI-962: Invoicing V3 users should automatically be navigated to the Estimates tab when landing on this page with no specified view type
		const invoicingVersion = user.Features.some(f => f.toLowerCase() === "invoicingv3") ? 3 : 2;
		this.NON_RKPAY_INVOICE_TYPES = nonRKPayInvoicesTypes(invoicingVersion);

		if (!isInvoicingV3) {
			this.NON_RKPAY_INVOICE_TYPES = this.NON_RKPAY_INVOICE_TYPES.filter(
				item => item.countKey !== "pending-jobs",
			);
		}

		let activeType = 1;
		if (query.paid === "true")
			activeType = nonRKPayInvoicesTypesIndex(invoicingVersion, "paid");
		else if (query.pendingJobs === "true")
			activeType = nonRKPayInvoicesTypesIndex(invoicingVersion, "pending-jobs");
		else if (query.pendingFinalization === "true")
			activeType = nonRKPayInvoicesTypesIndex(invoicingVersion, "pending-finalization");
		else if (query.payment === "true")
			activeType = nonRKPayInvoicesTypesIndex(invoicingVersion, "awaiting-payment");
		else if (query.estimate === "true" || invoicingVersion === 3)
			activeType = nonRKPayInvoicesTypesIndex(invoicingVersion, "estimates");
		let paid = false;
		let pendingJobs = false;
		let pendingFinalization = false;
		let payment = false;
		if (query.paid === "true") paid = true;
		else if (query.pendingJobs === "true") pendingJobs = true;
		else if (query.payment === "true") payment = true;
		else if (query.estimate === "true" || invoicingVersion === NON_RK_PAY_INVOICES_TYPES.PAID)
			paid = undefined;
		else if (query.pendingFinalization === "true") pendingFinalization = true;

		this.state = {
			buttonLabel: activeType === 0 ? "Create Estimate" : "Create Invoice",
			activeType,
			invoicingVersion,
			invoices: [],
			pageNumber: 1,
			hasMore: true,
			clients: {
				data: [],
				loading: false,
			},
			agencies: {
				data: [],
				loading: false,
			},
			expandDropdownId: null,
			filters: {
				archived: query.archived === "true",
				paid,
				pendingJobs,
				pendingFinalization,
				payment,
				isEstimate: activeType === 0 ? true : undefined,
				clientId: parseInt(query?.clientId, 10) || "",
				organizationId: query.organizationId || "",
				sortBy: query.sortBy || (invoicingVersion === 3 ? "IssueDate" : "DueDate"),
				asc: query.asc === undefined,
				voided: query.voided === "true",
				vehicleInvoicingRequired: query.vehicleInvoicingRequired === "true",
				startDate: "",
				endDate: "",
			},
			agencyRules: {
				RequireInvoicingForVehicles: false,
			},
			paidStatus: "All",
			emailingInvoice: undefined,
			downloadingInvoice: undefined,
			emailInvoiceModal: { show: false, clientEmailAddress: "" },
			selectedJobs: {},
			showAddToInvoice: false,
			selectedEstimateFinalized: false,
		};
		this.confirmationModal = React.createRef();
		this.dateMask = [/[0-1]/, /[0-9]/, "/", /[0-3]/, /[0-9]/, "/", /2/, /0/, /[2-9]/, /[0-9]/];
	}

	componentDidMount() {
		const {
			user,
			router: { push },
		} = this.props;
		const { activeType } = this.state;
		document.addEventListener("click", this.clickEventListener);

		const isInvoicingV3 = user.Features.some(f => f.toLowerCase() === FEATURES.INVOICING_V3);
		const isPaymentsTabV4Enabled = user.isSuperAdmin() || isInvoicingV3;

		if (isPaymentsTabV4Enabled) {
			setTimeout(() => push("/payments/v4"), 1);
		}

		if (user.Organization.NonRKPayInvoicingEnabled === true) {
			this.getClients();
			this.getAgencies();
			this.onTypeChange(activeType);
		} else {
			// If an RKPay user somehow manages to reach this page accidentally, force them to the regular payments list
			setTimeout(() => push("/payments/list/review"), 1);
		}

		this.getAgencyRules();
	}

	componentWillUnmount() {
		document.removeEventListener("click", this.clickEventListener);
		if (manualInvoicesCancel) manualInvoicesCancel();
		if (getClientsCancel) getClientsCancel();
	}

	/** Retrieves a list of clients */
	getClients = () => {
		const { lang } = global;
		const {
			user: {
				Organization: { Id: organizationId },
			},
		} = this.props;
		const { filters } = this.state;

		this.setState(state => ({ clients: { ...state.clients, loading: true } }));

		// If there is a request already running, abort it and start a new one
		if (getClientsCancel) {
			getClientsCancel();
		}
		ClientService.getAll(
			{ includeInactive: false, organizationId: filters.organizationId || organizationId },
			c => {
				getClientsCancel = c;
			},
		)
			.then(({ data }) => {
				getClientsCancel = undefined;
				const items = data.map(({ Id, Name }) => ({
					value: Id,
					label: Name,
				}));

				this.setState({ clients: { loading: false, data: items } });
			})
			.catch(error => {
				this.setState(state => ({ clients: { ...state.clients, loading: false } }));
				if (!axios.isCancel(error)) {
					utils.notify({
						type: "error",
						message: error.response?.data?.Message ?? lang.t("Error loading clients"),
						timeOut: 1500,
					});
				}
			});
	};

	/** Retrieves all agencies that the user can filter the job list by */
	getAgencies = () => {
		const { lang } = global;
		const { user } = this.props;

		// This select list is only available to Super Admins
		if (!user.isSuperAdmin()) return;

		this.setState(state => ({ agencies: { ...state.agencies, loading: true } }));

		// If there is a request already running, abort it and start a new one
		if (getAgenciesCancel) {
			getAgenciesCancel();
		}
		OrganizationService.getAll({ minimalData: true }, c => {
			getAgenciesCancel = c;
		})
			.then(({ data }) => {
				getAgenciesCancel = undefined;
				const agencies = data.map(({ Id: value, Name: label }) => ({ value, label }));
				this.setState({ agencies: { ...agencies, data: agencies } });
			})
			.catch(error => {
				this.setState(state => ({ agencies: { ...state.agencies, loading: false } }));
				if (!axios.isCancel(error)) {
					utils.notify({
						type: "error",
						message: error.response?.data?.Message ?? lang.t("Error loading clients"),
						timeOut: 1500,
					});
				}
			})
			.finally(() =>
				this.setState(state => ({ agencies: { ...state.agencies, loading: false } })),
			);
	};

	/**
	 * Retrieves a paginated list of invoices
	 * @param {Object} overrideFilters Used for overriding the pageNumber filter in order to paginate the list of invoices
	 * @param {Function} callback A callback method used for any subsequent functionality
	 */
	getInvoices = (overrideFilters = {}, callback = undefined) => {
		const { lang } = global;
		const { filters, pageNumber, activeType } = this.state;
		// Remove empty query parameters for the API request
		const validFilters = Object.fromEntries(
			Object.entries(filters).filter(item => item[1] !== ""),
		);

		if ("pendingFinalization" in validFilters) {
			validFilters.pending = Boolean(validFilters.pendingFinalization);
			delete validFilters.pendingFinalization;
		}

		const queryParams = {
			displayCount,
			pageNumber,
			...validFilters,
			...overrideFilters,
		};
		// If there is a request already running, abort it and start a new one
		if (manualInvoicesCancel) {
			manualInvoicesCancel();
		}

		if (activeType === NON_RK_PAY_INVOICES_TYPES.PENDING_JOBS) {
			JobService.getUnInvoicedJobs({ ...queryParams })
				.then(({ data }) => {
					const { selectedJobs } = this.state;
					const [anyInvoice = {}] = Object.values(selectedJobs);

					const newData = data?.ItemList ?? [];
					const invoices = newData.map(invoice => {
						const disabled =
							Object.keys(selectedJobs).length === 0
								? false
								: !(
										invoice.ClientId === anyInvoice.ClientId &&
										(invoice.ExcludeOfficerRate || false) ===
											(anyInvoice.ExcludeOfficerRate || false)
									);
						return { ...invoice, disabled };
					});

					this.setState(prevState => ({
						invoices:
							prevState.pageNumber > 1
								? [...prevState.invoices, ...invoices]
								: invoices,
						hasMore: invoices.length >= displayCount,
						pageNumber: prevState.pageNumber + 1,
					}));
				})
				.catch(error => {
					this.setState({ invoices: [], hasMore: false, pageNumber: 1 });
					if (!axios.isCancel(error)) {
						const { response } = error;

						utils.notify({
							type: "error",
							message: response?.data?.Message ?? lang.t("Error loading Job List"),
						});
					}
				});
		} else {
			PaymentService.getNonRKPayInvoices(
				c => {
					manualInvoicesCancel = c;
				}, // 'Get cancel token' function
				queryParams,
			)
				.then(({ data }) => {
					manualInvoicesCancel = undefined;

					if (callback) {
						callback(data);
					} else {
						const newData = data?.ItemList ?? [];
						this.setState(prevState => ({
							invoices:
								prevState.pageNumber > 1
									? [...prevState.invoices, ...newData]
									: newData,
							hasMore: newData.length >= displayCount,
							pageNumber: prevState.pageNumber + 1,
						}));
					}
				})
				.catch(error => {
					this.setState({ invoices: [], hasMore: false, pageNumber: 1 });
					if (!axios.isCancel(error)) {
						const { response } = error;

						utils.notify({
							type: "error",
							message:
								response?.data?.Message ?? lang.t("Error loading Payment List"),
						});
					}
				});
		}
	};

	getAgencyRules = async () => {
		const { lang } = global;
		const { user } = this.props;

		if (!user.isLEA()) return;

		try {
			const { data: agencyRules } = await OrganizationService.getJurisdictionAgencyRules();
			this.setState(() => ({ agencyRules }));
		} catch {
			utils.notify({
				type: "error",
				message: lang.t("Error getting Jurisdiction Agency Rules"),
				timeOut: 1500,
			});
		}
	};

	/** Updates the invoice status filter according to what the user clicks on (or selects on mobile) from the `<HeaderGrid>` component
	 * @param {activeType} type A string value indicating which invoice status the user would like to filter list results by.
	 * These values come from the `this.NON_RKPAY_INVOICE_TYPES`
	 */
	onTypeChange = activeType => {
		const { hasMore, invoices } = this.state;
		const {
			router: { push },
		} = this.props;
		const invoiceType = this.NON_RKPAY_INVOICE_TYPES.find(
			item => item.type === parseInt(activeType, 10),
		);
		// Set type filter and invoices = []
		// Also set hasMore = true to make the ScrollDetect component appear
		this.setState(
			state => {
				const newType = invoiceType?.type ?? 1;
				const filters = {
					...state.filters,
					...invoiceType.filter,
				};
				// Estimates (type === 0) do not have a due date column. If switching to Estimates view and sorting by due date,
				// change this to sort by issue date instead
				if (newType === 0 && filters.sortBy === "DueDate") {
					filters.sortBy = "IssueDate";
					filters.asc = true;
				}

				if (filters.voided === true && filters.archived === null) {
					filters.archived = false;
				}

				if (newType === NON_RK_PAY_INVOICES_TYPES.PENDING_JOBS) {
					filters.sortBy = "JobId";
					filters.asc = true;
				}

				return {
					activeType: newType,
					buttonLabel: newType === 0 ? "Create Estimate" : "Create Invoice",
					hasMore: true,
					invoices: [],
					pageNumber: 1,
					filters,
					paidStatus: "All",
					selectedJobs: {},
				};
			},
			() => {
				// If ScrollDetect is already visible in the viewport,
				// set type filter and request data immediately
				if (hasMore && invoices.length === 0 && manualInvoicesCancel) {
					manualInvoicesCancel();
					this.getInvoices();
				}

				push(this.buildNewUrl());
			},
		);
	};

	/**
	 * @param {Object} event An object returned from the`ReactSelect` component that contains `value` and `label` properties.
	 * `null` if selection is cleared.
	 */
	handleAgencyChange = event => {
		const {
			filters: { organizationId },
		} = this.state;
		const { router } = this.props;
		if (event?.value === organizationId) return;

		this.setState(
			prevState => ({
				hasMore: true,
				invoices: [],
				pageNumber: 1,
				filters: {
					...prevState.filters,
					organizationId: event?.value || "",
					clientId: "",
				},
				clients: {
					data: [],
					loading: false,
				},
			}),
			() => {
				this.getClients();
				this.getInvoices();
				router.push(this.buildNewUrl());
			},
		);
	};

	handleDateInputChange = (property, { target: { value } }) => {
		const { router } = this.props;
		// For date inputs on iOS, all '/' characters need to be replaced with '-'
		const formattedValue = utils.isIOSDevice ? value.replace(/\//gi, "-") : value;
		this.setState(
			prevState => ({ filters: { ...prevState.filters, [property]: formattedValue } }),
			() => {
				this.setState(
					{
						pageNumber: 1,
						jobs: [],
						selectedJobs: [],
						hasMore: false,
					},
					() => {
						// In case ScrollDetect is already visible in the viewport,
						// make new ones immediately (cancel tokens will cancel existing requests)
						this.getInvoices();
					},
				);

				router.push(this.buildNewUrl());
			},
		);
	};

	/** Filters the list of invoices by the selected client */
	onClientFilter = event => {
		const { router } = this.props;
		const clientId = event?.value ?? "";
		this.setState(
			state => ({
				hasMore: true,
				invoices: [],
				pageNumber: 1,
				filters: { ...state.filters, clientId },
			}),
			() => {
				router.push(this.buildNewUrl());
			},
		);
	};

	/** Filters the list of estimates by their paid status */
	onPaidFilter = event => {
		const {
			target: { value },
		} = event;
		let paid = value ?? "";
		let paidStatus = "All";
		switch (paid) {
			case "Paid":
				paid = true;
				paidStatus = value;
				break;
			case "Unpaid":
				paid = false;
				paidStatus = value;
				break;
			default:
				paid = undefined;
				break;
		}
		this.setState(prevState => ({
			hasMore: true,
			invoices: [],
			pageNumber: 1,
			filters: { ...prevState.filters, paid },
			paidStatus,
		}));
	};

	/**
	 * Calls an independent request to search for the invoice number
	 * If found the user is navigated to its invoice form
	 * @param {String} invoiceNumber The invoice number of the invoice being searched
	 * */
	onInvoiceNumberFilter = invoiceNumber => {
		const { lang } = global;
		const {
			location: { query },
			router,
		} = this.props;
		const { activeType } = this.state;

		if (invoiceNumber === "") return;

		if (activeType === NON_RK_PAY_INVOICES_TYPES.PENDING_JOBS) {
			router.push(`/jobs/${invoiceNumber}`);
			return;
		}

		this.getInvoices({ pageNumber: 1, invoiceNumber }, data => {
			if (data?.ItemList?.length > 0) {
				const [{ Id, IsEstimate }] = data.ItemList;
				router.push(
					`/payments/manualinvoice?id=${Id}${IsEstimate ? "&estimate=true" : ""}`,
				);
			} else {
				utils.notify({
					type: "error",
					message:
						query.estimate === "true"
							? lang.t("Estimate could not be found")
							: lang.t("Invoice could not be found"),
				});
			}
		});
	};

	onNewInvoice = () => {
		const { router } = this.props;
		const { activeType } = this.state;
		router.push(`/payments/manualinvoice${activeType === 0 ? "?estimate=true" : ""}`);
	};

	onEditInvoice = invoiceId => event => {
		if (event) event.stopPropagation();

		const { router } = this.props;
		const { activeType } = this.state;
		router.push(
			`/payments/manualinvoice?id=${invoiceId}${activeType === 0 ? "&estimate=true" : ""}`,
		);
	};

	/**
	 * Displays a modal which allows the user to directly apply a payment to an invoice
	 * @param {Number} invoiceId The unique id of the selected invoice
	 * * @param {bool} estimateFinalized Bool indicating if it's an estimate finalized
	 */
	onApplyPayment = (invoiceId, estimateFinalized) => event => {
		if (event) event.stopPropagation();

		this.setState({ selectedEstimateFinalized: estimateFinalized });

		this._applyPaymentsModal.openModal(invoiceId);
	};

	/**
	 * Applies any necessary updates to an invoice item after a payment has been applied ot it
	 * @param {Object} obj
	 * @param {Number} obj.id The unique id of the invoice
	 * @param {Number} obj.totalApplied The total payment amount applied to an invoice
	 */
	applyPaymentCallback = ({ id, totalApplied }) => {
		const {
			filters: { paid },
		} = this.state;
		this.setState(prevState => {
			// Update the appropriate invoice balance
			let invoices = prevState.invoices.map(invoice => {
				if (invoice.Id === id) return { ...invoice, Balance: invoice.Total - totalApplied };
				return invoice;
			});
			// Based on the current view (awaiting payment/paid), filter out any invoices that don't belong there anymore
			invoices = invoices.filter(invoice =>
				paid ? invoice.Balance <= 0 : invoice.Balance > 0,
			);
			return { invoices };
		});
	};

	/** When toggled, displays a list of archived invoices */
	toggleArchivedInvoices = () => {
		const { router } = this.props;
		this.setState(
			prevState => ({
				pageNumber: 1,
				filters: {
					...prevState.filters,
					archived: !prevState.filters.archived,
				},
			}),
			() => {
				this.getInvoices();
				router.push(this.buildNewUrl());
			},
		);
	};

	toggleVoidedInvoices = () => {
		const { router } = this.props;
		this.setState(
			prevState => ({
				pageNumber: 1,
				filters: {
					...prevState.filters,
					voided: !prevState.filters.voided,
				},
			}),
			() => {
				this.getInvoices();
				router.push(this.buildNewUrl());
			},
		);
	};

	toggleVehicleInvoicingRequired = () => {
		const { router } = this.props;
		this.setState(
			prevState => ({
				pageNumber: 1,
				filters: {
					...prevState.filters,
					vehicleInvoicingRequired: prevState.filters.vehicleInvoicingRequired
						? undefined
						: true,
				},
			}),
			() => {
				this.getInvoices();
				router.push(this.buildNewUrl());
			},
		);
	};

	onRowClick = invoiceId => event => {
		const preventNavigation = event?.target?.dataset?.preventNavigation;
		if (!preventNavigation) this.onEditInvoice(invoiceId)();
	};

	onRowKeyPress = invoiceId => event => {
		const preventNavigation = event?.target?.dataset?.preventNavigation;
		if (!preventNavigation && event.key === "Enter") this.onEditInvoice(invoiceId)();
	};

	onClickTab = ({ tabNumber, url }) => {
		const {
			router: { push },
		} = this.props;
		const thisTab = PAYMENTS_TAB_MENU.find(tab => tab.type === "manual");
		if (tabNumber !== thisTab.tabNumber) push(url);
	};

	/** Builds the new URL to be shown in the address bar, eliminating empty and redundant where possible query string values */
	buildNewUrl = () => {
		const { filters } = this.state;
		const queryParams = {};
		if (filters.organizationId) queryParams.organizationId = filters.organizationId;
		if (filters.clientId) queryParams.clientId = filters.clientId;
		if (filters.paid) queryParams.paid = true;
		if (filters.pendingFinalization) queryParams.pendingFinalization = true;
		if (filters.pendingJobs) queryParams.pendingJobs = true;
		if (filters.payment) queryParams.payment = true;
		if (filters.isEstimate) queryParams.estimate = true;
		if (filters.sortBy !== "DueDate") queryParams.sortBy = filters.sortBy;
		if (filters.asc === false) queryParams.asc = false;
		if (filters.voided) queryParams.voided = true;
		if (filters.archived) queryParams.archived = true;
		if (filters.vehicleInvoicingRequired) queryParams.vehicleInvoicingRequired = true;

		const queryString = new URLSearchParams(queryParams).toString();
		return `/payments/manual${queryString ? `?${queryString}` : ""}`;
	};

	/**
	 * Updates the sorting method for the list of invoices displayed. If the provided property is already the property being sorted on,
	 * this toggles the value of the `asc` filter.
	 * @param {String} property The property name to sort by. As per the API, it can be one of the following: InvoiceNumber, ClientName,
	 * IssueDate, SentDate, DueDate, Total, Balance
	 */
	updateSort = property => {
		const { hasMore, invoices } = this.state;
		const { router } = this.props;

		this.setState(
			prevState => {
				const { filters } = prevState;
				const { sortBy: prevSortBy, asc: prevAsc } = filters;
				if (prevSortBy !== property) filters.sortBy = property;
				else filters.asc = !prevAsc;

				return {
					pageNumber: 1,
					invoices: [],
					hasMore: true,
					filters,
				};
			},
			() => {
				// If ScrollDetect is already visible in the viewport, set type filter and request data immediately
				if (hasMore && invoices.length === 0 && manualInvoicesCancel) {
					manualInvoicesCancel();
					this.getInvoices();
				}

				router.push(this.buildNewUrl());
			},
		);
	};

	calculateTotal = item => {
		const { Total, Tax, ServiceFee, WorkerCompensationFee } = item;
		return Total + Tax + ServiceFee + (WorkerCompensationFee || 0);
	};

	calculateBalance = item => {
		const { Balance, Voided } = item;
		const total = this.calculateTotal(item);
		const balance = Balance - (Voided ? total : 0);

		return balance;
	};

	/** Listens for any clicks outside of the actions dropdown menu in order to handle closing the dropdown
	 * @param {Event} event The HTML event that informs us what was clicked on
	 */
	clickEventListener = ({ target }) => {
		const { expandDropdownId } = this.state;
		const actions = document.getElementById(`action-menu_${expandDropdownId}`);
		if (
			!target.closest(".btn-group") &&
			actions?.classList.contains("open") &&
			expandDropdownId !== null
		) {
			this.setState({ expandDropdownId: null });
		}
	};

	/** Toggles the button menu to display a list of actions can be performed by a user on an invoice
	 * @param {Number} id The id of element whose dropdown is toggled
	 */
	toggleActionDropdown = id => {
		let invoiceId = id;
		const actions = document.getElementById(`action-menu_${invoiceId}`);
		if (actions.classList.contains("open")) invoiceId = null;
		this.setState({ expandDropdownId: invoiceId });
	};

	handleInvoiceData = data => {
		this.setState({ emailInvoiceModal: { clientEmailAddress: data.ClientEmailAddress } });
	};

	getInvoiceDetails = id => {
		const { lang } = global;
		return InvoiceService.getNonRKPayInvoice(id)
			.then(({ data }) => {
				this.handleInvoiceData(data);
				return data;
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading invoice details"),
					timeOut: 2000,
				});
			});
	};

	/**
	 * Send the invoice to a specific client email
	 * @param {Number} id The unique id of the client
	 */
	sendInvoiceToClient = async id => {
		const { lang } = global;
		const invoiceDetails = await this.getInvoiceDetails(id);
		const sendInvoice = (additionalEmails = []) => {
			this.setState({ emailingInvoice: id });
			InvoiceService.sendManualInvoiceEmail(id, additionalEmails)
				.then(() => {
					utils.notify({
						type: "success",
						message: "Invoice sent to client",
						timeOut: 1500,
					});
					// Retrieve latest invoice details to ensure that the SentDateTimeUTC value is correct for future updates
					this.setState({
						emailingInvoice: undefined,
						emailInvoiceModal: {
							show: false,
						},
					});
				})
				.catch(() => {
					this.setState({ emailingInvoice: undefined });
					utils.notify({
						type: "error",
						message: "Unable to send Invoice to client",
						timeOut: 2000,
					});
				});
		};
		if (
			invoiceDetails.Version === 3 &&
			!invoiceDetails.IsEstimate &&
			!invoiceDetails.Finalized &&
			!invoiceDetails.Voided
		) {
			this.setState(prevState => ({
				emailInvoiceModal: {
					...prevState.emailInvoiceModal,
					show: true,
					title: lang.t("Finalize Invoice"),
					type: "finalize",
					message: (
						<div>
							<div>
								{`${lang.t("Would you like to finalize before sending to")}`}
								<strong>&nbsp;{invoiceDetails.ClientEmailAddress}</strong>?
							</div>
							{lang.t(
								"Once this invoice is finalized, no further modifications can be made_",
							)}
						</div>
					),
					confirmButtonText: lang.t("Finalize and Send"),
					onSubmit: additionalEmails =>
						this.finalizeInvoice(id, () => sendInvoice(additionalEmails)),
				},
			}));
		} else {
			this.setState(prevState => ({
				emailInvoiceModal: {
					...prevState.emailInvoiceModal,
					show: true,
					title: lang.t("E-mail Invoice"),
					type: "email",
					message: (
						<div>
							{lang.t("Invoice will be sent to")}
							<strong>&nbsp;{invoiceDetails.ClientEmailAddress}</strong>
						</div>
					),
					confirmButtonText: lang.t("Send"),
					onSubmit: additionalEmails => sendInvoice(additionalEmails),
				},
			}));
		}
	};

	finalizeInvoice = (invoiceId, callback) => {
		const { lang } = global;
		const updateInvoices = invoices => {
			const newData = invoices?.ItemList ?? [];
			this.setState(prevState => ({
				invoices: newData,
				hasMore: newData.length >= displayCount,
				pageNumber: prevState.pageNumber,
			}));
		};
		InvoiceService.finalizeManualInvoice(invoiceId)
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t("Invoice successfully finalized"),
					timeOut: 2000,
				});
				this.getInvoices({ pageNumber: 1 }, invoices => updateInvoices(invoices));
				if (callback) callback();
			})
			.catch(({ response: { data } }) => {
				utils.notify({
					type: "error",
					message: data?.Message || lang.t("Error finalizing invoice"),
					timeOut: 1500,
				});
			});
	};

	downloadInvoicePDF = async (id, invoiceNumber) => {
		const { lang } = global;
		const invoiceDetails = await this.getInvoiceDetails(id);
		const downloadInvoice = () => {
			this.setState({ downloadingInvoice: id });
			InvoiceService.downloadManualInvoicePDF(id)
				.then(({ data }) => {
					utils.downloadFromBlobResponse(data, `invoice_${invoiceNumber}.pdf`);
					utils.notify({
						type: "success",
						message: "Invoice downloaded correctly",
						timeOut: 1500,
					});
				})
				.catch(() => {
					utils.notify({
						type: "error",
						message: "Unable to download Invoice",
						timeOut: 2000,
					});
				})
				.finally(() => this.setState({ downloadingInvoice: undefined }));
		};
		if (
			invoiceDetails.Version === 3 &&
			!invoiceDetails.IsEstimate &&
			!invoiceDetails.Finalized &&
			!invoiceDetails.Voided
		) {
			this.confirmationModal.openModal({
				title: lang.t("Finalize Invoice"),
				message: (
					<div>
						{lang.t("Would you like to finalize before downloading_")}
						<br />
						{lang.t(
							"Once this invoice is finalized, no further modifications can be made_",
						)}
					</div>
				),
				confirmButton: lang.t("Finalize and Download"),
				onConfirm: () => this.finalizeInvoice(id, () => downloadInvoice()),
			});
		} else {
			downloadInvoice();
		}
	};

	openURLInNewTab = (token) => {
		const url = `${URLS.rollkallPortal}/quickPay/${token}`;
		window.open(url, '_blank', 'noopener noreferrer');
	}

	showFinancialAdminActions = (evaluatePrepay = false, isPrepay = false) => {
		const { user } = this.props;
		const { activeType } = this.state;
		const invoicingVersion = user.Features.some(f => f.toLowerCase() === "invoicingv3") ? 3 : 2;

		if (!user.IsFinancialAdmin) return false;

		if (invoicingVersion === 2) {
			switch (activeType) {
				case 0:
					if (!evaluatePrepay || (evaluatePrepay && isPrepay)) return true;
					return false;
				case 1:
					return true;
				case 2:
					return false;
				default:
					return false;
			}
		}

		switch (activeType) {
			case 0:
				if (!evaluatePrepay || (evaluatePrepay && isPrepay)) return true;
				return false;
			case 1:
				return false;
			case 2:
				return true;
			case 3:
				return false;
			default:
				return false;
		}
	};

	updateSelectedJobs = item => {
		const { selectedJobs } = this.state;
		let { invoices } = this.state;

		if (selectedJobs[item.Id]) {
			delete selectedJobs[item.Id];
		} else {
			selectedJobs[item.Id] = { ...item };
		}

		invoices = invoices.map(invoice => {
			const disabled =
				Object.keys(selectedJobs).length === 0
					? false
					: !(
							invoice.ClientId === item.ClientId &&
							(invoice.ExcludeOfficerRate || false) ===
								(item.ExcludeOfficerRate || false)
						);
			return { ...invoice, disabled };
		});

		this.setState({ selectedJobs, invoices });
	};

	cancelPendingJobs = () => {
		let { invoices } = this.state;
		invoices = invoices.map(invoice => ({ ...invoice, disabled: false }));

		this.setState({ selectedJobs: {}, invoices });
	};

	openJob = jobId => event => {
		const preventNavigation = event?.target?.dataset?.preventNavigation;

		if (!preventNavigation) {
			const { router } = this.props;
			router.push(`/jobs/${jobId}`);
		}
	};

	createInvoice = () => {
		const { router } = this.props;
		const { selectedJobs } = this.state;
		const [job] = Object.values(selectedJobs);
		const { ClientId } = job;
		const jobs = Object.values(selectedJobs)
			.map(({ Id }) => Id)
			.join(",");

		router.push(`/payments/manualinvoice?clientId=${ClientId}&estimate=false&jobs=${jobs}`);
	};

	addToInvoice = (selectedJob = null) => {
		let { selectedJobs, invoices } = this.state;

		if (selectedJob) {
			selectedJobs = {
				[selectedJob.Id]: { ...selectedJob },
			};

			invoices = invoices.map(invoice => {
				const disabled =
					Object.keys(selectedJobs).length === 0
						? false
						: !(
								invoice.ClientId === selectedJob.ClientId &&
								(invoice.ExcludeOfficerRate || false) ===
									(selectedJob.ExcludeOfficerRate || false)
							);
				return { ...invoice, disabled };
			});
		}

		this.setState({ showAddToInvoice: true, selectedJobs, invoices });
	};

	render() {
		const { lang } = global;
		const {
			activeType,
			invoicingVersion,
			invoices,
			hasMore,
			clients,
			agencies,
			filters,
			buttonLabel,
			paidStatus,
			expandDropdownId,
			emailingInvoice,
			downloadingInvoice,
			emailInvoiceModal,
			selectedJobs,
			showAddToInvoice,
			agencyRules,
			selectedEstimateFinalized,
		} = this.state;
		const {
			user,
			router,
		} = this.props;

		const isInvoicingV2 =
			user.Organization.NonRKPayInvoicingEnabled || user.Organization.NonRKPayPaymentsEnabled;
		const isInvoicingV3 = user.Features.some(f => f.toLowerCase() === FEATURES.INVOICING_V3);

		let paymentTabs =
			invoicingVersion === 3 || isInvoicingV2
				? PAYMENTS_TAB_MENU.map(item => {
						if (item.type === "manual") return { ...item, text: lang.t("Agency") };
						return { ...item, text: lang.t("Officer") };
					})
				: PAYMENTS_TAB_MENU;

		const isPaymentsTabV4Enabled = user.isSuperAdmin() || isInvoicingV3;

		if (isPaymentsTabV4Enabled) {
			paymentTabs = PAYMENTS_V4_TAB_MENU.map(item => {
				if (item.type === "v4") return { ...item, text: lang.t("Agency") };
				return { ...item, text: lang.t("Officer") };
			});
		}

		const paidFilterOptions = ["All", "Paid", "Unpaid"];
		let goTo;
		switch (activeType) {
			case NON_RK_PAY_INVOICES_TYPES.ESTIMATES:
				goTo = "Estimate";
				break;
			case NON_RK_PAY_INVOICES_TYPES.PENDING_JOBS:
				goTo = "Job";
				break;
			default:
				goTo = "Invoice";
				break;
		}

		return (
			<Fragment>
				<TabMenu
					tabs={paymentTabs}
					activeTab={
						paymentTabs.find(
							tab => tab.type === (isPaymentsTabV4Enabled ? "v4" : "manual"),
						).tabNumber
					}
					onClickTab={this.onClickTab}
					user={user}
				/>
				<div
					className="container"
					id="non-rkpay-payments"
				>
					<div className="row">
						<div className="col-sm-1" />
						<div className="col-sm-10 col-xs-12">
							<HeaderGrid
								gridOptions={this.NON_RKPAY_INVOICE_TYPES}
								activeGrid={activeType}
								setActive={this.onTypeChange}
								showCounts={false}
							/>
						</div>
						<div className="col-sm-1" />
					</div>

					<div className="row form-group">
						<If
							condition={
								!user.isSuperAdmin() &&
								activeType !== NON_RK_PAY_INVOICES_TYPES.PENDING_JOBS
							}
						>
							<div className="col-xs-5 text-left">
								<button
									type="button"
									id="non-rkpay-new-invoice"
									data-test-id="payments_page__create_invoice_btn"
									className="btn btn-primary"
									onClick={this.onNewInvoice}
								>
									{lang.t(`${buttonLabel}`)}
								</button>
							</div>
						</If>
						<div
							className={`col-md-3 col-sm-4 col-xs-7 col-xs-offset-0 text-right ${
								user.isSuperAdmin() ||
								activeType === NON_RK_PAY_INVOICES_TYPES.PENDING_JOBS
									? "col-md-offset-9 col-sm-offset-8"
									: "col-md-offset-4 col-sm-offset-3"
							}`}
						>
							<SearchField
								placeholder={lang.t("dynamic.Go to", {
									place: `${lang.t(goTo)} #`,
								})}
								onSearch={this.onInvoiceNumberFilter}
								iconClass="rkf rkf-arrow-right"
								clearable
							/>
						</div>
					</div>

					<div className="invoice-filters">
						<div className="row">
							<div className="col-xs-12">
								<h3>{lang.t("Filters")}</h3>
							</div>
							<If
								condition={
									user.isSuperAdmin() &&
									activeType !== NON_RK_PAY_INVOICES_TYPES.PENDING_JOBS
								}
							>
								<div className="col-sm-3 form-group">
									<label
										className="control-label"
										htmlFor="non-rkpay-name-filter"
									>
										{lang.t("Agency")}
									</label>
									<Select
										name="non-rkpay-agency-filter"
										placeholder={lang.t("Search by Name")}
										value={parseInt(filters.organizationId, 10)}
										onChange={this.handleAgencyChange}
										options={agencies.data}
										isLoading={agencies.loading}
										isClearable
									/>
								</div>
							</If>
							<div
								className={classNames("col-sm-3 form-group", {
									"margin-top-25":
										activeType === NON_RK_PAY_INVOICES_TYPES.PENDING_JOBS,
								})}
							>
								<label
									className="control-label"
									htmlFor="non-rkpay-name-filter"
								>
									{lang.t("Client")}
								</label>
								<Select
									name="non-rkpay-client-filter"
									placeholder={lang.t("Search by Name")}
									value={filters.clientId}
									onChange={this.onClientFilter}
									options={clients.data}
									isLoading={clients.loading}
									isClearable
									data-test-id="payments_page__client_dropdown"
								/>
							</div>
							<If condition={activeType === NON_RK_PAY_INVOICES_TYPES.PENDING_JOBS}>
								<div className="col-sm-3 form-group">
									<div className="flex flex-direction-column">
										<label htmlFor="dateRange">{lang.t("Date Range")}</label>
										<label htmlFor="dateRangeStart">
											{lang.t("Start Date")}
										</label>
									</div>
									<Choose>
										<When condition={utils.isDesktopSafari || utils.isIE}>
											<MaskedInput
												name="dateRangeStart"
												id="dateRangeStart"
												type="text"
												mask={this.dateMask}
												className="form-control"
												placeholder="mm/dd/yyyy"
												value={filters.startDate}
												onChange={event =>
													this.handleDateInputChange("startDate", event)
												}
											/>
										</When>
										<Otherwise>
											<input
												name="dateRangeStart"
												id="dateRangeStart"
												type="date"
												className="form-control"
												value={filters.startDate}
												onChange={event =>
													this.handleDateInputChange("startDate", event)
												}
											/>
										</Otherwise>
									</Choose>
								</div>
								<div
									className={classNames("col-sm-3 form-group", {
										"margin-top-25":
											activeType === NON_RK_PAY_INVOICES_TYPES.PENDING_JOBS,
									})}
								>
									<label htmlFor="dateRangeEnd">{lang.t("End Date")}</label>
									<Choose>
										<When condition={utils.isDesktopSafari || utils.isIE}>
											<MaskedInput
												name="dateRangeEnd"
												id="dateRangeEnd"
												type="text"
												mask={this.dateMask}
												className="form-control"
												placeholder="mm/dd/yyyy"
												value={filters.endDate}
												onChange={event =>
													this.handleDateInputChange("endDate", event)
												}
											/>
										</When>
										<Otherwise>
											<input
												name="dateRangeEnd"
												id="dateRangeEnd"
												type="date"
												className="form-control"
												min={filters.startDate}
												value={filters.endDate}
												onChange={event =>
													this.handleDateInputChange("endDate", event)
												}
											/>
										</Otherwise>
									</Choose>
								</div>
							</If>
							{/* Estimates */}
							<If condition={activeType === NON_RK_PAY_INVOICES_TYPES.ESTIMATES}>
								<div className="col-sm-3 form-group">
									<label
										className="control-label"
										htmlFor="paymentStatus"
									>
										{lang.t("Paid Status")}
									</label>
									<select
										id="paymentStatus"
										name="paymentStatus"
										className="form-control"
										placeholder={lang.t("Paid Status")}
										value={paidStatus}
										onChange={this.onPaidFilter}
										data-test-id="payments_page__paid_status_dropdown"
									>
										{paidFilterOptions.map(option => (
											<option
												key={`${option}`}
												value={option}
											>
												{option}
											</option>
										))}
									</select>
								</div>
							</If>
							<If
								condition={
									invoicingVersion === 3 &&
									activeType !== NON_RK_PAY_INVOICES_TYPES.PENDING_JOBS
								}
							>
								<div className="col-xs-6 col-sm-3 col-md-2 form-group checkbox">
									<br />
									<label htmlFor="voidedFilter">
										<input
											name="voidedFilter"
											type="checkbox"
											id="voidedFilter"
											checked={filters.voided === true}
											onChange={this.toggleVoidedInvoices}
										/>
										{lang.t("Only Voided")}
									</label>
								</div>
							</If>
							<If
								condition={
									(invoicingVersion === 3 &&
										(activeType === NON_RK_PAY_INVOICES_TYPES.ESTIMATES || activeType === this.NON_RKPAY_INVOICE_TYPES.PAID)
									)
									||
									(invoicingVersion === 2 &&
										(activeType === NON_RK_PAY_INVOICES_TYPES.ESTIMATES || activeType === 2)
									)
								}
							>
								{/* Paid */}
								<div className="col-xs-6 col-sm-3 col-md-2 form-group checkbox">
									<br />
									<label htmlFor="archiveFilter">
										<input
											name="archiveFilter"
											type="checkbox"
											id="archiveFilter"
											checked={filters.archived === true}
											onChange={() => this.toggleArchivedInvoices()}
											data-test-id="payments_page__include_archived_checkbox"
										/>
										{lang.t("Only Archived")}
									</label>
								</div>
							</If>
							<If
								condition={
									agencyRules.RequireInvoicingForVehicles &&
									activeType !== NON_RK_PAY_INVOICES_TYPES.ESTIMATES
								}
							>
								<div className="col-xs-6 col-sm-3 col-md-2 form-group checkbox">
									<br />
									<label htmlFor="vehicleInvocingFilter">
										<input
											name="vehicleInvocingFilter"
											type="checkbox"
											id="vehicleInvocingFilter"
											checked={filters.vehicleInvoicingRequired === true}
											onChange={() => this.toggleVehicleInvoicingRequired()}
										/>
										{lang.t("Vehicle Invoicing Required")}
									</label>
								</div>
							</If>
						</div>
					</div>

					<div
						className="row hidden-xs hidden-sm invoice-columns"
						id="non-rkpay-payments-table-header"
					>
						<Choose>
							<When condition={activeType === NON_RK_PAY_INVOICES_TYPES.PENDING_JOBS}>
								<div className="col-sm-1" />
								<div
									className="col-md-1"
									role="button"
									tabIndex="0"
									onClick={() => this.updateSort("JobId")}
									onKeyPress={({ key }) => {
										if (key === "Enter") this.updateSort("JobId");
									}}
								>
									<strong>{lang.t("Job")} #</strong>
									<If condition={filters.sortBy === "JobId"}>
										<i
											className={`glyphicon glyphicon-triangle-${filters.asc ? "top" : "bottom"}`}
										/>
									</If>
								</div>
								<div
									className="col-md-2"
									role="button"
									tabIndex="0"
									onClick={() => this.updateSort("Client")}
									onKeyPress={({ key }) => {
										if (key === "Enter") this.updateSort("Client");
									}}
								>
									<strong>{lang.t("Client")}</strong>
									<If condition={filters.sortBy === "Client"}>
										<i
											className={`glyphicon glyphicon-triangle-${filters.asc ? "top" : "bottom"}`}
										/>
									</If>
								</div>
								<div
									className="col-md-2"
									role="button"
									tabIndex="0"
									onClick={() => this.updateSort("JobDate")}
									onKeyPress={({ key }) => {
										if (key === "Enter") this.updateSort("JobDate");
									}}
								>
									<strong>{lang.t("Job Date")}</strong>
									<If condition={filters.sortBy === "JobDate"}>
										<i
											className={`glyphicon glyphicon-triangle-${filters.asc ? "top" : "bottom"}`}
										/>
									</If>
								</div>
								<div
									className="col-md-2"
									role="button"
									tabIndex="0"
									onClick={() => this.updateSort("JobName")}
									onKeyPress={({ key }) => {
										if (key === "Enter") this.updateSort("JobName");
									}}
								>
									<strong>{lang.t("Job Name")}</strong>
									<If condition={filters.sortBy === "JobName"}>
										<i
											className={`glyphicon glyphicon-triangle-${filters.asc ? "top" : "bottom"}`}
										/>
									</If>
								</div>
								<div className="col-md-4" />
							</When>
							<Otherwise>
								<div
									className="col-md-2"
									role="button"
									tabIndex="0"
									onClick={() => this.updateSort("InvoiceNumber")}
									onKeyPress={({ key }) => {
										if (key === "Enter") this.updateSort("InvoiceNumber");
									}}
								>
									<strong>
										{lang.t(activeType === 0 ? "Estimate" : "Invoice")} #
									</strong>
									<If condition={filters.sortBy === "InvoiceNumber"}>
										<i
											className={`glyphicon glyphicon-triangle-${filters.asc ? "top" : "bottom"}`}
										/>
									</If>
								</div>
								<div
									className={activeType === 0 ? "col-md-3" : "col-md-2"}
									role="button"
									tabIndex="0"
									onClick={() => this.updateSort("ClientName")}
									onKeyPress={({ key }) => {
										if (key === "Enter") this.updateSort("ClientName");
									}}
								>
									<strong>{lang.t("Client")}</strong>
									<If condition={filters.sortBy === "ClientName"}>
										<i
											className={`glyphicon glyphicon-triangle-${filters.asc ? "top" : "bottom"}`}
										/>
									</If>
								</div>
								<div
									className="col-md-1"
									role="button"
									tabIndex="0"
									onClick={() => this.updateSort("IssueDate")}
									onKeyPress={({ key }) => {
										if (key === "Enter") this.updateSort("IssueDate");
									}}
								>
									<strong>{lang.t("Issued")}</strong>
									<If condition={filters.sortBy === "IssueDate"}>
										<i
											className={`glyphicon glyphicon-triangle-${filters.asc ? "top" : "bottom"}`}
										/>
									</If>
								</div>
								<div
									role="button"
									tabIndex="0"
									onClick={() => this.updateSort("SentDate")}
									onKeyPress={({ key }) => {
										if (key === "Enter") this.updateSort("SentDate");
									}}
									className="col-md-1"
								>
									<strong>{lang.t("Sent")}</strong>
									<If condition={filters.sortBy === "SentDate"}>
										<i
											className={`glyphicon glyphicon-triangle-${filters.asc ? "top" : "bottom"}`}
										/>
									</If>
								</div>
								<div
									role="button"
									tabIndex="0"
									onClick={() => this.updateSort("DueDate")}
									onKeyPress={({ key }) => {
										if (key === "Enter") this.updateSort("DueDate");
									}}
									className={activeType === 0 ? "hidden" : "col-md-1"}
								>
									<strong>{lang.t("Due")}</strong>
									<If condition={filters.sortBy === "DueDate"}>
										<i
											className={`glyphicon glyphicon-triangle-${filters.asc ? "top" : "bottom"}`}
										/>
									</If>
								</div>
								<div
									className="col-md-1 text-right"
									role="button"
									tabIndex="0"
									onClick={() => this.updateSort("Total")}
									onKeyPress={({ key }) => {
										if (key === "Enter") this.updateSort("Total");
									}}
								>
									<strong>{lang.t("Total")}</strong>
									<If condition={filters.sortBy === "Total"}>
										<i
											className={`glyphicon glyphicon-triangle-${filters.asc ? "top" : "bottom"}`}
										/>
									</If>
								</div>
								<div
									className="col-md-1 text-right"
									role="button"
									tabIndex="0"
									onClick={() => this.updateSort("Balance")}
									onKeyPress={({ key }) => {
										if (key === "Enter") this.updateSort("Balance");
									}}
								>
									<strong>{lang.t("Balance")}</strong>
									<If condition={filters.sortBy === "Balance"}>
										<i
											className={`glyphicon glyphicon-triangle-${filters.asc ? "top" : "bottom"}`}
										/>
									</If>
								</div>
								<div className="col-md-2" />
							</Otherwise>
						</Choose>
					</div>

					<div className="row">
						<div
							className="container"
							id="non-rkpay-payments-table-body"
						>
							<For
								each="item"
								of={invoices}
							>
								<Choose>
									<When
										condition={
											activeType === NON_RK_PAY_INVOICES_TYPES.PENDING_JOBS
										}
									>
										<div
											key={`job-item_${item?.Id}`}
											className={classNames("row v-align-sm", {
												disabled: item.disabled,
											})}
											onClick={this.openJob(item.Id)}
											onKeyPress={this.openJob(item.Id)}
											role="button"
											tabIndex={0}
										>
											<div className="checkbox col-sm-1">
												<label htmlFor={`checkbox_${item?.Id}`}>
													<input
														type="checkbox"
														id={`checkbox_${item.Id}`}
														name="selectInvoice"
														checked={selectedJobs[item.Id]}
														onChange={() =>
															this.updateSelectedJobs(item)
														}
														onClick={e => e.stopPropagation()}
														aria-label="Select Job"
														disabled={item.disabled}
													/>
												</label>
											</div>

											<div
												className="col-sm-12 col-md-1 truncate"
												title={item.Id}
											>
												{item.Id}
											</div>

											<div
												className="col-sm-12 col-md-2 truncate"
												title={item.ClientName ?? "N/A"}
											>
												{item.ClientName ?? "N/A"}
											</div>

											<div
												className="col-sm-12 col-md-2 truncate"
												title={item.StartDateTime}
											>
												{utils.formatDate(
													`${item.StartDateTime}Z`,
													"en-US",
													user.TimeZoneId,
													"numeric",
												)}
											</div>

											<div
												className="col-sm-12 col-md-2"
												title={item.Name}
											>
												<div className="truncate">
													<span>{item.Name}</span>
												</div>
												<If
													condition={
														agencyRules.RequireInvoicingForVehicles &&
														item.VehicleInvoicingRequired
													}
												>
													<span className="label label-warning">
														{lang.t("Vehicle Fees")}
													</span>
												</If>
											</div>

											<div className="col-sm-12 col-md-2" />

											<div className="col-xs-12 col-sm-2 text-right">
												<button
													type="button"
													className="btn btn-primary btn-block add-to__invoice"
													onClick={() => this.addToInvoice(item)}
													data-prevent-navigation
													disabled={item.disabled}
												>
													{lang.t("Add to Invoice")}
												</button>
											</div>
										</div>
									</When>
									<Otherwise>
										<div
											className={`${item.Archived ? "archiveColor" : ""} row v-align-sm`}
											role="button"
											key={`non_rkpay_invoice_${item.Id}`}
											tabIndex={0}
											onClick={this.onRowClick(item.Id)}
											onKeyPress={this.onRowKeyPress(item.Id)}
										>
											<div
												className="col-sm-12 col-md-2 truncate"
												title={item.InvoiceNumber}
											>
												{item.InvoiceNumber}
											</div>

											<div
												className={`${activeType === 0 ? "col-md-3" : "col-md-2"} col-sm-12 truncate`}
												title={item.ClientName ?? "N/A"}
											>
												{item.ClientName ?? "N/A"}
											</div>

											<div className="col-sm-3 col-md-1 issued-date text-right">
												<span className="visible-xs-inline visible-sm-inline">
													{lang.t("Issued")}
													&nbsp;
												</span>
												{utils.formatDate(
													item.IssueDate,
													"en-US",
													user.TimeZoneId,
													"numeric",
												)}
											</div>
											<div
												className={classNames("col-md-1 text-right", {
													"col-sm-push-3 col-md-push-0":
														!item.SentDateTimeUTC,
													"col-sm-9": activeType === 0,
													"col-sm-3": activeType !== 0,
												})}
											>
												<If condition={item.SentDateTimeUTC}>
													<span className="visible-xs-inline visible-sm-inline">
														{lang.t("Sent")}
														&nbsp;
													</span>
													{utils.formatDate(
														item.SentDateTimeUTC,
														"en-US",
														user.TimeZoneId,
														"numeric",
													)}
												</If>
											</div>
											<div
												className={classNames(
													"col-sm-6 col-md-1 text-right",
													{
														hidden: activeType === 0,
														"col-sm-pull-3 col-md-pull-0":
															!item.SentDateTimeUTC,
													},
												)}
											>
												<span className="visible-xs-inline visible-sm-inline">
													{lang.t("Due")}
													&nbsp;
												</span>
												{utils.formatDate(
													item.DueDate,
													"en-US",
													user.TimeZoneId,
													"numeric",
												)}
											</div>

											<div className="col-sm-3 col-md-1 amounts total text-right">
												<span className="visible-xs-inline-block visible-sm-inline-block">
													{`${lang.t("Total")}:`}
													&nbsp;
												</span>
												{utils.currencyFormatter(
													utils.bankersRound(this.calculateTotal(item)),
												)}
												<If
													condition={
														item.RequirePrepayment && item.Version === 3
													}
												>
													&nbsp;
													<span className="label label-success">
														{lang.t("Prepay")}
													</span>
												</If>
												<If
													condition={
														item.VehicleInvoicingRequired &&
														item.Version === 3
													}
												>
													&nbsp;
													<span className="label label-warning">
														{lang.t("Vehicle Fees")}
													</span>
												</If>
											</div>

											<div className="col-sm-3 col-md-1 amounts text-right">
												<span className="visible-xs-inline-block visible-sm-inline-block">
													{`${lang.t("Balance")}:`}
													&nbsp;
												</span>
												{utils.currencyFormatter(
													utils.bankersRound(this.calculateBalance(item)),
												)}
											</div>
											<Choose>
												<When condition={user.isSuperAdmin()}>
													<If
														condition={
															!this.showFinancialAdminActions()
														}
													>
														<div
															className={classNames(
																"col-xs-12 col-sm-2 text-right",
																{
																	"col-sm-offset-4 col-md-offset-1":
																		item.SentDateTimeUTC,
																	"col-sm-offset-1":
																		!item.SentDateTimeUTC,
																},
															)}
														>
															<button
																type="button"
																className="btn btn-primary btn-block"
																onClick={this.onEditInvoice(
																	item.Id,
																)}
																data-prevent-navigation
															>
																{lang.t("View")}
															</button>
														</div>
													</If>
													<If
														condition={this.showFinancialAdminActions()}
													>
														<div
															id={`action-menu_${item.Id}`}
															className={classNames(
																"btn-group col-xs-12 col-sm-2 text-right col-sm-offset-1",
																{
																	open:
																		expandDropdownId !== null &&
																		expandDropdownId ===
																			item.Id,
																},
															)}
															data-prevent-navigation
														>
															<div className="actions-menu">
																<button
																	type="button"
																	name="actionsDropdown"
																	className="btn btn-primary dropdown-toggle"
																	onClick={e => {
																		e.preventDefault();
																		this.toggleActionDropdown(
																			item.Id,
																		);
																	}}
																	aria-haspopup="true"
																	aria-expanded="false"
																	data-prevent-navigation
																>
																	<span data-prevent-navigation>
																		{lang.t("Actions")}
																		&nbsp;
																		<span
																			className="caret"
																			data-prevent-navigation
																		/>
																	</span>
																</button>
															</div>
															<ul className="dropdown-menu">
																<li>
																	<button
																		type="button"
																		className="btn btn-block text-left"
																		onClick={this.onEditInvoice(
																			item.Id,
																		)}
																		data-prevent-navigation
																	>
																		{lang.t("View")}
																	</button>
																</li>
																<If
																	condition={
																		this.showFinancialAdminActions(
																			true,
																			item.RequirePrepayment,
																		) &&
																		item.Balance > 0 &&
																		!item.Voided
																	}
																>
																	<li>
																		<button
																			type="button"
																			className="btn btn-block text-left"
																			onClick={() =>
																				this.openURLInNewTab(
																					item.PaymentToken,
																				)
																			}
																			data-prevent-navigation
																		>
																			Pay Now
																		</button>
																	</li>
																</If>
															</ul>
														</div>
													</If>
												</When>
												<Otherwise>
													<Choose>
														<When
															condition={
																activeType ===
																	NON_RK_PAY_INVOICES_TYPES.AWAITING_PAYMENT ||
																activeType ===
																	NON_RK_PAY_INVOICES_TYPES.PENDING_FINALIZATION ||
																activeType ===
																	NON_RK_PAY_INVOICES_TYPES.PAID
															}
														>
															<div
																id={`action-menu_${item.Id}`}
																className={classNames(
																	"btn-group col-md-3",
																	{
																		open:
																			expandDropdownId !==
																				null &&
																			expandDropdownId ===
																				item.Id,
																	},
																)}
																data-prevent-navigation
															>
																<div className="actions-menu">
																	<button
																		type="button"
																		name="actionsDropdown"
																		className="btn btn-primary"
																		onClick={e => {
																			e.preventDefault();
																			this.toggleActionDropdown(
																				item.Id,
																			);
																		}}
																		aria-haspopup="true"
																		aria-expanded="false"
																		data-prevent-navigation
																	>
																		<i
																			className={`rkf rkf-spinner rkf-spin rkf-fw${emailingInvoice === item.Id || downloadingInvoice === item.Id ? "" : " hidden"}`}
																		/>
																		<span
																			className={
																				emailingInvoice ===
																					item.Id ||
																				downloadingInvoice ===
																					item.Id
																					? "hidden"
																					: undefined
																			}
																			data-prevent-navigation
																		>
																			{lang.t("Actions")}
																			&nbsp;
																			<span
																				className="caret"
																				data-prevent-navigation
																			/>
																		</span>
																	</button>
																</div>
																<ul className="dropdown-menu">
																	<If
																		condition={activeType === 3}
																	>
																		<li>
																			<button
																				type="button"
																				className="btn btn-block text-left"
																				onClick={this.onEditInvoice(
																					item.Id,
																				)}
																				data-prevent-navigation
																			>
																				{lang.t("View")}
																			</button>
																		</li>
																	</If>
																	<If
																		condition={activeType !== 3}
																	>
																		<li>
																			<button
																				type="button"
																				className="btn btn-block text-left"
																				onClick={this.onEditInvoice(
																					item.Id,
																				)}
																				data-prevent-navigation
																			>
																				{lang.t(
																					"Edit Invoice",
																				)}
																			</button>
																		</li>
																	</If>
																	<li>
																		<button
																			type="button"
																			className="btn btn-block text-left"
																			onClick={() =>
																				this.sendInvoiceToClient(
																					item.Id,
																				)
																			}
																			data-prevent-navigation
																		>
																			{lang.t("EMail")}
																			&nbsp;
																			{lang.t("Invoice")}
																		</button>
																	</li>
																	<li>
																		<button
																			type="button"
																			id="download-invoice"
																			className="btn btn-block text-left"
																			onClick={() =>
																				this.downloadInvoicePDF(
																					item.Id,
																					item.InvoiceNumber,
																				)
																			}
																			data-prevent-navigation
																		>
																			{lang.t("Download")}
																			&nbsp;
																			{lang.t("Invoice")}
																		</button>
																	</li>
																	<If
																		condition={activeType === 3}
																	>
																		<li>
																			<button
																				type="button"
																				id="download-invoice"
																				className="btn btn-block text-left"
																				onClick={this.onApplyPayment(
																					item.Id,
																					item.EstimateFinalized
																				)}
																				data-prevent-navigation
																				disabled={
																					item.Archived
																				}
																			>
																				{lang.t("Payments")}
																				&nbsp;
																				{lang.t("History")}
																			</button>
																		</li>
																	</If>
																</ul>
															</div>
														</When>
														<When
															condition={
																activeType ===
																NON_RK_PAY_INVOICES_TYPES.PENDING_JOBS
															}
														>
															<div>sdad</div>
														</When>
														<Otherwise>
															<div className="col-sm-4 col-sm-offset-2 col-md-3 col-md-offset-0">
																<div className="row">
																	<div className="col-xs-6">
																		<button
																			type="button"
																			className="btn btn-primary btn-block"
																			onClick={this.onEditInvoice(
																				item.Id,
																			)}
																			data-prevent-navigation
																		>
																			{lang.t("Edit")}
																		</button>
																	</div>
																	<div className="col-xs-6">
																		<button
																			type="button"
																			className="btn btn-primary btn-block"
																			onClick={this.onApplyPayment(
																				item.Id,
																				item.EstimateFinalized
																			)}
																			data-prevent-navigation
																			disabled={item.Archived}
																		>
																			{lang.t("Payments")}
																		</button>
																	</div>
																</div>
															</div>
														</Otherwise>
													</Choose>
												</Otherwise>
											</Choose>
										</div>
									</Otherwise>
								</Choose>
							</For>
							<If condition={hasMore}>
								<ScrollDetect
									className="text-center"
									onVisible={this.getInvoices}
								>
									<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
								</ScrollDetect>
							</If>
						</div>

						{/* Bulk Invoice Actions */}
						<div
							className={`bulk-actions ${Object.keys(selectedJobs).length > 0 ? "show-actions" : ""}`}
						>
							<div className="row">
								<div className="col-sm-offset-6 col-sm-6 col-md-offset-7 col-md-5 text-right">
									<button
										type="button"
										className="btn btn-cancel"
										onClick={() => this.cancelPendingJobs()}
									>
										{lang.t("Cancel")}
									</button>
									<button
										type="button"
										className="btn btn-default"
										onClick={() => this.createInvoice()}
									>
										{lang.t("Create Invoice")}
									</button>
									<button
										type="button"
										className="btn btn-default"
										onClick={() => this.addToInvoice()}
									>
										{lang.t("Add to Invoice")}
									</button>
								</div>
							</div>
						</div>
						{/* Bulk Invoice Actions */}
					</div>
					<ApplyPaymentsModal
						ref={ref => {
							this._applyPaymentsModal = ref;
						}}
						callback={this.applyPaymentCallback}
						user={user}
						invoiceVersion={invoicingVersion}
						disabledManualPayments={selectedEstimateFinalized}
					/>
					<EmailInvoiceModal
						billingEmail={emailInvoiceModal.clientEmailAddress}
						show={emailInvoiceModal.show}
						title={emailInvoiceModal.title}
						type={emailInvoiceModal.type}
						message={emailInvoiceModal.message}
						confirmButtonText={emailInvoiceModal.confirmButtonText}
						onSubmit={emailInvoiceModal.onSubmit}
						loading={emailingInvoice !== undefined}
						onCancel={() => {
							this.setState(prevState => ({
								...prevState,
								emailInvoiceModal: { show: false },
							}));
						}}
					/>
					<AddToInvoiceModal
						show={showAddToInvoice}
						onHide={() => this.setState({ showAddToInvoice: false })}
						router={router.push}
						jobs={Object.values(selectedJobs)}
						user={user}
						onCreateNewInvoice={this.createInvoice}
					/>
					<ConfirmationModal
						ref={ref => {
							this.confirmationModal = ref;
						}}
					/>
				</div>
			</Fragment>
		);
	}
}

NonRKPayInvoiceList.propTypes = {
	user: PropTypes.object,
	location: PropTypes.object,
	router: PropTypes.object.isRequired,
};

NonRKPayInvoiceList.defaultProps = {
	user: {},
	location: {},
};

export default NonRKPayInvoiceList;
